(function () {
  const CM_PER_INCH = 2.54;
  const MEASUREMENTS = ['chest', 'neck', 'back'];
  const MEASUREMENT_WEIGHTS = { chest: 2, neck: 1, back: 1.5 };
  const RANGE_EPSILON = 0.01;
  const TOTAL_STEPS = 3;

  function formatCm(value) {
    return String(Math.round(Number(value)));
  }

  function formatInches(value) {
    const inches = Number(value) / CM_PER_INCH;
    const rounded = Math.round(inches * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  function toCm(value, unit) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return unit === 'in' ? numeric * CM_PER_INCH : numeric;
  }

  function getRange(dimension) {
    if (!dimension) return null;
    if (dimension.min != null && dimension.max != null) {
      const min = Number(dimension.min);
      const max = Number(dimension.max);
      if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0) return null;
      return { min: Math.min(min, max), max: Math.max(min, max) };
    }
    if (dimension.value != null) {
      const value = Number(dimension.value);
      if (!Number.isFinite(value) || value <= 0) return null;
      return { min: value, max: value };
    }
    return null;
  }

  function getRangeCenter(size, key) {
    const range = getRange(size[key]);
    if (!range) return null;
    return (range.min + range.max) / 2;
  }

  function getNeighbourCenter(sizes, index, key, direction, fitGroup) {
    for (let i = index + direction; i >= 0 && i < sizes.length; i += direction) {
      if (sizes[i].fitGroup !== fitGroup) continue;

      const center = getRangeCenter(sizes[i], key);
      if (center != null) return center;
    }

    return null;
  }

  function inferExactValueRange(sizes, size, index, key) {
    const range = getRange(size[key]);
    if (!range || range.min !== range.max) return range;

    const value = range.min;
    const previous = getNeighbourCenter(sizes, index, key, -1, size.fitGroup);
    const next = getNeighbourCenter(sizes, index, key, 1, size.fitGroup);

    if (previous == null && next == null) return range;

    const lowerGap = previous != null ? value - previous : next - value;
    const upperGap = next != null ? next - value : value - previous;

    return {
      min: Math.max(0.1, value - Math.max(lowerGap, 0) / 2),
      max: value + Math.max(upperGap, 0) / 2,
    };
  }

  function normalizeSizes(sizes) {
    return sizes.map((size, index) => {
      const normalized = { ...size };

      MEASUREMENTS.forEach((key) => {
        normalized[key] = inferExactValueRange(sizes, size, index, key);
      });

      return normalized;
    });
  }

  function getDisplayLabel(size, includeFitLabel) {
    if (!size) return null;
    if (!includeFitLabel || !size.fitLabel) return size.label || null;
    return `${size.fitLabel}: ${size.label}`;
  }

  function hasMultipleFitGroups(sizes) {
    const groups = new Set(sizes.map((size) => size.fitGroup).filter(Boolean));
    return groups.size > 1;
  }

  function scoreSize(measurements, size) {
    let allFit = true;
    let totalDistance = 0;
    let fitScore = 0;
    let comparedCount = 0;

    MEASUREMENTS.forEach((key) => {
      const value = measurements[key];
      const range = getRange(size[key]);
      if (value == null || !range) return;

      comparedCount += 1;

      const weight = MEASUREMENT_WEIGHTS[key] || 1;
      const center = (range.min + range.max) / 2;
      const halfRange = Math.max((range.max - range.min) / 2, 1);
      fitScore += (Math.abs(value - center) / halfRange) * weight;

      if (value < range.min - RANGE_EPSILON) {
        allFit = false;
        totalDistance += (range.min - value) * weight;
      } else if (value > range.max + RANGE_EPSILON) {
        allFit = false;
        totalDistance += (value - range.max) * weight;
      }
    });

    if (comparedCount === 0) {
      return { allFit: false, totalDistance: Number.POSITIVE_INFINITY, fitScore: Number.POSITIVE_INFINITY };
    }

    return { allFit, totalDistance, fitScore };
  }

  function compareScoredSizes(a, b) {
    return (
      a.totalDistance - b.totalDistance ||
      a.fitScore - b.fitScore ||
      Number(b.isCurrentProduct) - Number(a.isCurrentProduct) ||
      a.index - b.index
    );
  }

  function getLooserSize(sizes, recommendedIndex) {
    const recommended = sizes[recommendedIndex];
    if (!recommended) return null;

    return (
      sizes
        .slice(recommendedIndex + 1)
        .find((size) => size.fitGroup === recommended.fitGroup) || null
    );
  }

  function recommendSize(measurements, sizes) {
    if (!sizes.length) {
      return { recommended: null, looser: null, imperfect: false };
    }

    const normalizedSizes = normalizeSizes(sizes);
    const includeFitLabel = hasMultipleFitGroups(normalizedSizes);
    const scored = normalizedSizes.map((size, index) => ({
      ...size,
      index,
      ...scoreSize(measurements, size),
    }));

    const fitting = scored.filter((entry) => entry.allFit).sort(compareScoredSizes);
    const sorted = fitting.length > 0 ? fitting : [...scored].sort(compareScoredSizes);
    const recommended = sorted[0] || null;
    const looser = recommended ? getLooserSize(normalizedSizes, recommended.index) : null;

    return {
      recommended,
      recommendedLabel: getDisplayLabel(recommended, includeFitLabel),
      looser,
      looserLabel: getDisplayLabel(looser, includeFitLabel),
      imperfect: fitting.length === 0,
    };
  }

  function setUnit(modal, unit) {
    modal.dataset.unit = unit;

    modal.querySelectorAll('.size-guide-modal__units-btn').forEach((button) => {
      const isActive = button.dataset.unit === unit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function selectProductSize(sectionId, sizeLabel) {
    if (!sizeLabel) return false;

    const variantSelects = document.querySelector(`#variant-selects-${sectionId}`);
    if (!variantSelects) return false;

    const normalizedLabel = sizeLabel.trim().toLowerCase();
    const inputs = variantSelects.querySelectorAll(
      'fieldset.product-form__input input[type="radio"], .product-form__input--dropdown select'
    );

    for (const input of inputs) {
      if (input.tagName === 'SELECT') {
        const option = Array.from(input.options).find(
          (entry) => entry.value.trim().toLowerCase() === normalizedLabel
        );
        if (!option) continue;
        input.value = option.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      if (input.value.trim().toLowerCase() === normalizedLabel) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    return false;
  }

  function initSizeCalculatorModal(modal) {
    if (modal.dataset.sizeCalculatorInit === 'true') return;
    modal.dataset.sizeCalculatorInit = 'true';

    const sectionId = modal.dataset.sectionId;
    const sizesNode = modal.querySelector('[data-calculator-sizes]');
    const sizes = sizesNode ? JSON.parse(sizesNode.textContent) : [];

    const stepsPanel = modal.querySelector('[data-calculator-panel="steps"]');
    const resultsPanel = modal.querySelector('[data-calculator-panel="results"]');
    const stepElements = Array.from(modal.querySelectorAll('[data-calculator-step]'));
    const progressDots = Array.from(modal.querySelectorAll('[data-progress-dot]'));
    const nextButton = modal.querySelector('[data-calculator-next]');
    const backButton = modal.querySelector('[data-calculator-back]');
    const recommendedOutput = modal.querySelector('[data-calculator-recommended]');
    const looserOutput = modal.querySelector('[data-calculator-looser]');
    const looserWrap = modal.querySelector('[data-calculator-looser-wrap]');
    const imperfectNote = modal.querySelector('[data-calculator-imperfect]');
    const selectButtons = modal.querySelectorAll('[data-calculator-select]');
    const unitsToggle = modal.querySelector('.size-guide-modal__units-toggle');

    let currentStep = 1;
    let showingResults = false;
    let lastRecommendation = { recommended: null, looser: null };

    function getActiveInput() {
      return modal.querySelector(`[data-calculator-input="${MEASUREMENTS[currentStep - 1]}"]`);
    }

    function getMeasurementsInCm() {
      const unit = modal.dataset.unit || 'cm';
      const measurements = {};

      MEASUREMENTS.forEach((key) => {
        const input = modal.querySelector(`[data-calculator-input="${key}"]`);
        measurements[key] = input ? toCm(input.value, unit) : null;
      });

      return measurements;
    }

    function updateChrome() {
      modal.classList.toggle('size-calculator-modal--results', showingResults);
    }

    function hasEnteredMeasurements() {
      return MEASUREMENTS.some((key) => {
        const input = modal.querySelector(`[data-calculator-input="${key}"]`);
        return input && input.value.trim() !== '';
      });
    }

    function updateProgress() {
      progressDots.forEach((dot, index) => {
        const stepNumber = index + 1;
        const isComplete = showingResults || stepNumber < currentStep;
        const isActive = !showingResults && stepNumber === currentStep;
        dot.classList.toggle('is-active', isActive);
        dot.classList.toggle('is-complete', isComplete);
      });
    }

    function showStep(step) {
      showingResults = false;
      currentStep = step;

      stepElements.forEach((element) => {
        const isActive = Number(element.dataset.calculatorStep) === step;
        element.classList.toggle('hidden', !isActive);
        element.toggleAttribute('hidden', !isActive);
      });

      stepsPanel.hidden = false;
      stepsPanel.classList.remove('hidden');
      resultsPanel.hidden = true;
      resultsPanel.classList.add('hidden');

      backButton.hidden = step <= 1;
      nextButton.textContent =
        step === TOTAL_STEPS ? modal.dataset.labelSeeResults : modal.dataset.labelNext;
      nextButton.dataset.action = 'next';

      updateChrome();
      updateProgress();

      const input = getActiveInput();
      if (input) {
        window.requestAnimationFrame(() => input.focus());
      }
    }

    function showResults() {
      const measurements = getMeasurementsInCm();
      const result = recommendSize(measurements, sizes);
      lastRecommendation = result;

      showingResults = true;
      stepsPanel.hidden = true;
      stepsPanel.classList.add('hidden');
      resultsPanel.hidden = false;
      resultsPanel.classList.remove('hidden');

      recommendedOutput.textContent = result.recommendedLabel || '—';
      looserOutput.textContent = result.looserLabel || '—';

      const hasLooser = Boolean(result.looserLabel);
      looserWrap.hidden = !hasLooser;
      imperfectNote.hidden = !result.imperfect;

      backButton.hidden = false;
      nextButton.textContent = modal.dataset.labelStartOver;
      nextButton.dataset.action = 'restart';

      selectButtons.forEach((button) => {
        const type = button.dataset.calculatorSelect;
        const size = type === 'looser' ? result.looser : result.recommended;
        button.hidden = !size;
        button.dataset.sizeLabel = size?.label || '';
        button.dataset.sizeUrl = size?.url || '';
        button.dataset.isCurrentProduct = size?.isCurrentProduct ? 'true' : 'false';
      });

      updateChrome();
      updateProgress();
    }

    function resetCalculator() {
      MEASUREMENTS.forEach((key) => {
        const input = modal.querySelector(`[data-calculator-input="${key}"]`);
        if (!input) return;
        input.value = '';
        input.classList.remove('size-calculator-modal__input--error');
        input.setAttribute('aria-invalid', 'false');
      });

      nextButton.dataset.action = 'next';
      showStep(1);
    }

    function validateCurrentStep() {
      const input = getActiveInput();
      if (!input) return false;

      const unit = modal.dataset.unit || 'cm';
      const value = toCm(input.value, unit);
      const isValid = value != null;

      input.classList.toggle('size-calculator-modal__input--error', !isValid);
      input.setAttribute('aria-invalid', isValid ? 'false' : 'true');

      return isValid;
    }

    unitsToggle?.addEventListener('click', (event) => {
      const button = event.target.closest('.size-guide-modal__units-btn');
      if (!button || button.classList.contains('is-active')) return;

      event.stopImmediatePropagation();

      const newUnit = button.dataset.unit;
      const shouldRestart = showingResults || currentStep > 1 || hasEnteredMeasurements();

      if (shouldRestart) {
        resetCalculator();
      }

      setUnit(modal, newUnit);
    });

    nextButton?.addEventListener('click', () => {
      if (nextButton.dataset.action === 'restart') {
        resetCalculator();
        return;
      }

      if (showingResults) return;

      if (!validateCurrentStep()) return;

      if (currentStep < TOTAL_STEPS) {
        showStep(currentStep + 1);
        return;
      }

      if (!sizes.length) return;
      showResults();
    });

    backButton?.addEventListener('click', () => {
      if (showingResults) {
        nextButton.dataset.action = 'next';
        showStep(TOTAL_STEPS);
        return;
      }

      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    });

    selectButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const sizeLabel = button.dataset.sizeLabel;
        const sizeUrl = button.dataset.sizeUrl;
        const isCurrentProduct = button.dataset.isCurrentProduct !== 'false';

        if (!isCurrentProduct && sizeUrl) {
          window.location.href = sizeUrl;
          return;
        }

        if (selectProductSize(sectionId, sizeLabel)) {
          modal.hide();
        } else if (sizeUrl) {
          window.location.href = sizeUrl;
        }
      });
    });

    modal.querySelectorAll('[data-calculator-input]').forEach((input) => {
      input.addEventListener('input', () => {
        input.classList.remove('size-calculator-modal__input--error');
        input.setAttribute('aria-invalid', 'false');
      });
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open' && modal.hasAttribute('open')) {
          setUnit(modal, 'cm');
          resetCalculator();
        }
      });
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    setUnit(modal, 'cm');
    showStep(1);
  }

  function initSizeCalculatorModals() {
    document.querySelectorAll('modal-dialog.size-calculator-modal').forEach(initSizeCalculatorModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSizeCalculatorModals);
  } else {
    initSizeCalculatorModals();
  }
})();
